import { useToastStore } from '@branch-messenger/willow-ui'
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useMatchRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { use } from 'react'

import {
  getUser,
  getWorker,
  login,
  loginWorker,
  logout,
  resendOtp,
  sendEmailVerification,
  verifyLogin,
} from '@/api/auth'
import { getTrackID } from '@/api/registration'
import { DialogContext } from '@/context'
import { handleHttpException } from '@/lib/httpException'
import {
  identifyUser,
  sendAnalyticsEvent,
  setUserProperties,
} from '@/lib/mixpanel'
import { formatPhoneNumber } from '@/lib/phone'
import { queryClient } from '@/lib/query-client'
import { cleanUpCookieandStorage } from '@/lib/token'
import { useOrganizationStore } from '@/store/organization/organization'
import { LoginRequestBody, ResendOTP, VerifyLoginOTP } from '@/types/auth'
import { OldIResponse } from '@/types/common'
import { GetTrackIdPayload } from '@/types/registration'

import { GET_DISBURSEMENTS_QUERY } from './disbursements'
import {
  EWA_BRANCH_VERIFICATION_QUERY,
  EWA_PAYROLL_SCHEDULES_QUERY,
  EWA_TRANSACTION_BALANCE_QUERY,
  GET_ADVANCES_QUERY,
  REPORTED_EARNINGS_QUERY,
} from './ewa'
import { ORGANIZATION_INFO_QUERY } from './organization'
import { GET_PAYMENT_PROFILE_QUERY } from './profile'

export const useSendEmailVerificationMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => await sendEmailVerification(email),
  })
}

export const LOGIN_MUTATION_KEY = 'LOGIN_MUTATION_KEY'
export const useLogin = (handleEmailVerificationError?: () => void) => {
  const navigate = useNavigate()
  const { showAlert } = use(DialogContext)
  const handleLoginError = () => {
    showAlert({
      title: 'Unable To Login',
      description: 'Please check your information and try again.',
      actionDescription: 'OK',
      hideCancel: true,
    })
  }
  return useMutation({
    mutationFn: async (data: LoginRequestBody) => {
      const res = await login(data)
      return res.data
    },
    mutationKey: [LOGIN_MUTATION_KEY],
    onSuccess: () => sendAnalyticsEvent('AUTH', 'login:success'),

    onError: (error, payload) => {
      sendAnalyticsEvent('AUTH', 'login:failure')
      handleHttpException(error, {
        onHttpError: error => {
          if (error.response?.status === 412) {
            if (handleEmailVerificationError) {
              return handleEmailVerificationError()
            }
            navigate({
              to: '/setup/email-verification',
              state: { loginRequestBody: payload },
            })
          } else {
            handleLoginError()
          }
        },
        onOtherError: handleLoginError,
      })
    },
  })
}

export const useResendCode = () => {
  const addToast = useToastStore(state => state.addToast)
  return useMutation({
    mutationFn: async (data: ResendOTP) => {
      const res = await resendOtp(data)
      return res.data
    },
    onSuccess: (_, { verification_channel }) => {
      const method = verification_channel === 'PHONE' ? 'Text' : 'Email'
      addToast({
        title: `${method} successfully sent`,
        variant: 'success',
      })
    },
    onError: () => {
      addToast({
        title: `Error sending code`,
        variant: 'alert',
      })
    },
  })
}

export const useVerifyCode = () => {
  const { showAlert } = use(DialogContext)

  return useMutation({
    mutationFn: async (data: VerifyLoginOTP) => {
      const res = await verifyLogin(data)
      return res.data
    },

    onSuccess: (response, payload) => {
      const { user_id } = response
      identifyUser(user_id)
      setUserProperties({ $email: payload?.email })
    },

    onError: () => {
      showAlert({
        title: 'Unable To Login',
        description: 'Please check your information and try again.',
        actionDescription: 'OK',

        hideCancel: true,
      })
    },
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()
  const { showAlert } = use(DialogContext)
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async () => {
      const res = await logout()
      return res.data
    },

    onSuccess: () => {
      cleanUpCookieandStorage()
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_INFO_QUERY] })
      navigate({ to: '/login' })
    },

    onError: () => {
      showAlert({
        title: 'Unable To Logout',
        description: 'Please try again.',
        actionDescription: 'OK',

        hideCancel: true,
      })
    },
  })
}

export const useLoginUserMutation = () => {
  const setOrgId = useOrganizationStore(state => state.setOrgId)
  const navigate = useNavigate()
  const { showAlert } = use(DialogContext)
  const match = useMatchRoute()
  const newAccount = !!match({ to: '/setup/email-verification' })

  return useMutation({
    mutationFn: async () => {
      const workerInfo = await queryClient.ensureQueryData(
        workerInfoQueryOptions
      )
      await loginWorker(workerInfo[0])
      return workerInfo
    },
    onSuccess: workerInfo => {
      const orgId = workerInfo[0].org_uuid as string
      setOrgId(orgId)
      if (newAccount) {
        return navigate({ to: '/account/debit-card' })
      } else {
        return navigate({ to: '/account' })
      }
    },
    onError: () => {
      showAlert({
        title: 'Unable To Login',
        description: 'Please check your information and try again.',
        actionDescription: 'OK',
        handleAction: () => {
          cleanUpCookieandStorage()
          navigate({ to: '/login' })
        },
        hideCancel: true,
      })
    },
  })
}

export const GET_WORKER_INFO_QUERY = 'get-worker-info-query'

export const workerInfoQueryOptions = queryOptions({
  queryKey: [GET_WORKER_INFO_QUERY],
  queryFn: async () => {
    const { data } = await getWorker()
    return data
  },
})

export const USER_QUERY = 'user-query'

export const userQueryOptions = queryOptions({
  queryKey: [USER_QUERY],
  queryFn: async () => {
    const { data } = await getUser()
    const { phone } = data || {}
    setUserProperties({
      $email: data.email,
      $name: data.full_name,
      $phone: data.phone,
    })

    return { ...data, formattedPhone: formatPhoneNumber(phone) }
  },
})

export const GET_TRACK_ID_QUERY_KEY = 'GET_TRACK_ID_QUERY_KEY'
export const getTrackIdQueryOptions = (payload: GetTrackIdPayload) =>
  queryOptions({
    queryKey: [GET_TRACK_ID_QUERY_KEY, payload],
    queryFn: async () => {
      const { data } = await getTrackID(payload)
      return data
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

export const useSwitchOrganization = () => {
  const setOrgId = useOrganizationStore(state => state.setOrgId)
  const { showAlert } = use(DialogContext)
  const router = useRouter()
  const { data: workerOrganizations } = useSuspenseQuery(workerInfoQueryOptions)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orgUuid: string) => {
      const { employee_id, org_id } =
        workerOrganizations.find(org => org.org_uuid === orgUuid) || {}
      if (!employee_id || !org_id) {
        throw new Error('Invalid orgId')
      }
      return loginWorker({
        employee_id,
        org_id,
      })
    },
    onSuccess: (_, orgUuid) => {
      setOrgId(orgUuid)
      router.invalidate() //re-run route loader
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey[0]
          return (
            queryKey === GET_DISBURSEMENTS_QUERY ||
            queryKey === GET_ADVANCES_QUERY ||
            queryKey === REPORTED_EARNINGS_QUERY ||
            queryKey === GET_PAYMENT_PROFILE_QUERY ||
            queryKey === EWA_BRANCH_VERIFICATION_QUERY ||
            queryKey === EWA_TRANSACTION_BALANCE_QUERY ||
            queryKey === EWA_PAYROLL_SCHEDULES_QUERY
          )
        },
      })
    },
    onError: error => {
      handleHttpException<OldIResponse>(error, {
        onHttpError: error => {
          showAlert({
            title: 'Unable To Switch Organization',
            description: error.response?.data?.message,
            actionDescription: 'OK',

            hideCancel: true,
          })
        },
        onOtherError: () => {
          showAlert({
            title: 'Unable To Switch Organization',
            description:
              'An error occurred switching organizations please try again later.',
            actionDescription: 'OK',

            hideCancel: true,
          })
        },
      })
    },
  })
}
